import { api } from "./client";
import type {
  AuthResponse,
  Ingredient,
  User,
  Venue,
  Menu,
  Category,
  Item,
  VenueTheme,
  QrCode,
  AnalyticsSummary,
  PopularItem,
  PlanInfo,
  ThemeResponse,
  Order,
  OrderStatus,
  LoyaltyProgram,
  LoyaltyRule,
  LoyaltyCustomer,
  Review,
} from "@/types";

// Auth
export const register = (data: { email: string; name: string; phone: string; venueName: string; password: string }) =>
  api.post<AuthResponse & { venueNumber: number }>("/auth/register", data).then((r) => r.data);

export const login = (venueNumber: number, password: string) =>
  api.post<AuthResponse>("/auth/login", { venueNumber, password }).then((r) => r.data);

export const refreshAuthToken = (refreshToken: string) =>
  api
    .post<AuthResponse>("/auth/refresh", { refreshToken })
    .then((r) => r.data);

export const logoutApi = (refreshToken: string) =>
  api.post<{ success: boolean }>("/auth/logout", { refreshToken }).then((r) => r.data);

export const getMe = () =>
  api.get<{ user: User }>("/auth/me").then((r) => r.data.user);

export const verifyEmail = (email: string, code: string) =>
  api.post("/auth/verify-email", { email, code }).then((r) => r.data);

export const resendVerification = (email: string) =>
  api.post("/auth/resend-verification", { email }).then((r) => r.data);

export const forgotPassword = (email: string) =>
  api.post("/auth/forgot-password", { email }).then((r) => r.data);

export const forgotNumber = (email: string) =>
  api.post("/auth/forgot-number", { email }).then((r) => r.data);

export const resetPassword = (email: string, code: string, password: string) =>
  api.post("/auth/reset-password", { email, code, password }).then((r) => r.data);

export const exportUserData = () =>
  api.get("/auth/export", { responseType: "blob" }).then((r) => r.data);

export const deleteAccount = () =>
  api.delete("/auth/account").then((r) => r.data);

// Venues
export const getVenues = () =>
  api.get<{ venues: Venue[] }>("/venues").then((r) => r.data.venues);

export const getVenue = (id: string) =>
  api.get<{ venue: Venue }>(`/venues/${id}`).then((r) => r.data.venue);

export const createVenue = (data: Partial<Venue>) =>
  api.post<{ venue: Venue }>("/venues", data).then((r) => r.data.venue);

export const updateVenue = (id: string, data: Partial<Venue>) =>
  api.patch<{ venue: Venue }>(`/venues/${id}`, data).then((r) => r.data.venue);

// Yemeksepeti — beta. `yemeksepetiEnabled: true` ile admin tarafından
// açılmış venue'ler için. Restoran sahibi credentials'ı PATCH /venues/:id
// (updateVenue) üzerinden `yemeksepetiConfig` field'ı ile kaydeder.
export const getYemeksepetiOrders = (venueId: string) =>
  api.get<{ orders: Array<Record<string, unknown>> }>(
    `/venues/${venueId}/yemeksepeti/orders`, { skipAuthHandling: true }
  ).then((r) => r.data.orders);

// Trendyol GO — beta. `trendyolgoEnabled: true` ile admin tarafından açılmış
// venue'ler için. Restoran sahibi credentials'ı (sellerId/apiKey/apiSecret)
// PATCH /venues/:id (updateVenue) üzerinden `trendyolgoConfig` ile kaydeder.
export const getTrendyolgoOrders = (venueId: string) =>
  api.get<{ orders: Array<Record<string, unknown>> }>(
    `/venues/${venueId}/trendyolgo/orders`, { skipAuthHandling: true }
  ).then((r) => r.data.orders);

// Restoran (mağaza) bilgilerini çeker — bağlantı/auth testi.
export const getTrendyolgoStores = (venueId: string) =>
  api.get<{ restaurants: Array<Record<string, unknown>>; raw: unknown }>(
    `/venues/${venueId}/trendyolgo/stores`, { skipAuthHandling: true }
  ).then((r) => r.data);

// Sipariş dinlemeyi (polling) aç/kapat.
export const setTrendyolgoListening = (venueId: string, enabled: boolean) =>
  api.patch<{ listening: boolean }>(
    `/venues/${venueId}/trendyolgo/listening`, { enabled }, { skipAuthHandling: true }
  ).then((r) => r.data);

// Bu işletmeye düşmüş Trendyol GO siparişleri (bizim DB'mizden).
export const getTrendyolgoRecentOrders = (venueId: string) =>
  api.get<{ orders: Order[] }>(
    `/venues/${venueId}/trendyolgo/recent-orders`, { skipAuthHandling: true }
  ).then((r) => r.data.orders);

// Statü push-back (Trendyol'a bildir + bizde güncelle).
export const acceptTrendyolgoOrder = (venueId: string, orderId: string, preparationTime?: number) =>
  api.post<{ ok: boolean; status: string }>(
    `/venues/${venueId}/trendyolgo/orders/${orderId}/accept`, { preparationTime }, { skipAuthHandling: true }
  ).then((r) => r.data);

export const prepareTrendyolgoOrder = (venueId: string, orderId: string) =>
  api.post<{ ok: boolean; status: string }>(
    `/venues/${venueId}/trendyolgo/orders/${orderId}/prepared`, undefined, { skipAuthHandling: true }
  ).then((r) => r.data);

export const cancelTrendyolgoOrder = (venueId: string, orderId: string, reasonId?: number) =>
  api.post<{ ok: boolean; status: string }>(
    `/venues/${venueId}/trendyolgo/orders/${orderId}/cancel`, { reasonId }, { skipAuthHandling: true }
  ).then((r) => r.data);


// Canlı slug uygunluk kontrolü — settings'de input'a yazılırken debounced
// çağrılır. `exclude` = mevcut venueId, kendi slug'ı "kullanılıyor"
// görünmesin diye.
export const checkVenueSlug = (slug: string, exclude?: string) =>
  api.get<{ available: boolean; reason?: "missing" | "too_short" | "reserved"; normalized?: string }>(
    "/venues/check-slug",
    { params: { slug, exclude } }
  ).then((r) => r.data);

export const deleteVenue = (id: string) =>
  api.delete(`/venues/${id}`).then((r) => r.data);

export const checkDns = (id: string) =>
  api.get<{ ok: boolean; records?: string[]; type?: string; error?: string }>(`/venues/${id}/check-dns`).then((r) => r.data);

// Menus
export const getMenus = (venueId: string) =>
  api.get<{ menus: Menu[] }>(`/venues/${venueId}/menus`).then((r) => r.data.menus);

export const createMenu = (venueId: string, data: { name: string }) =>
  api.post<{ menu: Menu }>(`/venues/${venueId}/menus`, data).then((r) => r.data.menu);

export const updateMenu = (id: string, data: Partial<Menu>) =>
  api.patch<{ menu: Menu }>(`/menus/${id}`, data).then((r) => r.data.menu);

export const deleteMenu = (id: string) =>
  api.delete(`/menus/${id}`).then((r) => r.data);

export const reorderMenus = (venueId: string, ids: string[]) =>
  api.patch(`/venues/${venueId}/menus/reorder`, { ids }).then((r) => r.data);

// Backgrounds (public)
export const getStockBackgrounds = () =>
  api.get<{ backgrounds: { _id: string; name: string; url: string; paddingTop?: number; paddingBottom?: number; paddingX?: number; paddingTopDesktop?: number; paddingBottomDesktop?: number; paddingXDesktop?: number }[] }>("/backgrounds").then((r) => r.data.backgrounds);

// AI
export const aiTranslate = (venueId: string, data: { name: string; description: string; sourceLang: string; targetLangs: string[] }) =>
  api.post<{ nameTranslations: Record<string, string>; descTranslations: Record<string, string> }>(`/venues/${venueId}/ai/translate`, data).then((r) => r.data);

export const aiGenerateDescription = (venueId: string, data: { name: string; lang: string; ingredients?: string[] }) =>
  api.post<{ description: string }>(`/venues/${venueId}/ai/generate-description`, data).then((r) => r.data);

export const aiGenerateImage = (venueId: string, data: { name: string; description?: string; itemId?: string; bgStyle?: string }) =>
  api.post<{ url: string }>(`/venues/${venueId}/ai/generate-image`, data).then((r) => r.data);

export const aiReimageImage = (venueId: string, data: { imageUrl: string; bgStyle?: string; itemId?: string }) =>
  api.post<{ url: string }>(`/venues/${venueId}/ai/reimagine-image`, data).then((r) => r.data);

export const getAiBgStyles = () =>
  api.get<{ styles: { id: string; label: string }[] }>("/ai/bg-styles").then((r) => r.data.styles);

export const parseMenu = (venueId: string, html: string, url?: string) =>
  api.post<{ categories: { name: string; items: { name: string; description?: string; price: number }[] }[] }>(`/venues/${venueId}/ai/parse-menu`, { html, url }).then((r) => r.data);

// Görsellerden menü çıkar (OpenAI vision). Backend en fazla 10 dosya kabul
// eder, her biri max 5 MB; sharp ile 1600px JPEG'e indirir, tek prompt'ta
// vision modeline gönderir. Kredi: image başına `menuImageParse` (default 5).
export const parseMenuImages = (venueId: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("images", f);
  return api
    .post<{ categories: { name: string; items: { name: string; description?: string; price: number }[] }[] }>(
      `/venues/${venueId}/ai/parse-menu-images`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    )
    .then((r) => r.data);
};

export const importMenu = (venueId: string, data: { menuName: string; categories: { name: string; items: { name: string; description?: string; price: number }[] }[] }) =>
  api.post<{ success: boolean; menuId: string; categories: number; items: number }>(`/venues/${venueId}/ai/import-menu`, data).then((r) => r.data);

export const getAiCredits = (venueId: string) =>
  api.get<{ credits: number; costs: { translate: number; image: number; reimagine: number; description: number } }>(`/venues/${venueId}/ai/credits`).then((r) => r.data);

// Announcement
export const getAnnouncement = (venueId: string) =>
  api.get<{ announcement: Announcement | null }>(`/venues/${venueId}/announcement`).then((r) => r.data.announcement);

export const createAnnouncement = (venueId: string, data: { image: string; title?: string; titleTranslations?: Record<string, string>; description?: string; descTranslations?: Record<string, string> }) =>
  api.post(`/venues/${venueId}/announcement`, data).then((r) => r.data);

export const deleteAnnouncement = (venueId: string) =>
  api.delete(`/venues/${venueId}/announcement`).then((r) => r.data);

export interface Announcement {
  _id: string;
  image: string;
  title?: string;
  titleTranslations?: Record<string, string>;
  description?: string;
  descTranslations?: Record<string, string>;
  isActive: boolean;
}

// Banners
export const getBanners = (venueId: string) =>
  api.get<{ banners: Banner[] }>(`/venues/${venueId}/banners`).then((r) => r.data.banners);

export const createBanner = (venueId: string, data: Partial<Banner>) =>
  api.post(`/venues/${venueId}/banners`, data).then((r) => r.data);

export const updateBanner = (id: string, data: Partial<Banner>) =>
  api.patch(`/banners/${id}`, data).then((r) => r.data);

export const deleteBanner = (id: string) =>
  api.delete(`/banners/${id}`).then((r) => r.data);

export interface Banner {
  _id: string;
  image: string;
  title?: string;
  titleTranslations?: Record<string, string>;
  menuId?: string;
  categoryId?: string;
  externalUrl?: string;
  startsAt?: string;
  endsAt?: string;
  isActive: boolean;
  sortOrder: number;
}

// Payment
export const initiatePayment = (data: { venueId: string; planType: string; period: "monthly" | "yearly" }) =>
  api.post<{ checkoutFormContent: string; token: string }>("/payment/initiate", data).then((r) => r.data);

export const getPaymentHistory = () =>
  api.get<{ payments: Array<{ _id: string; planType: string; period: string; amount: number; status: string; createdAt: string }> }>("/payment/history").then((r) => r.data.payments);

export const getCreditPackages = () =>
  api.get<{ packages: { _id: string; credits: number; priceTry: number; priceUsd: number }[] }>("/payment/credit-packages").then((r) => r.data.packages);

export const buyCredits = (data: { venueId: string; packageId: string }) =>
  api.post<{ checkoutFormContent: string; token: string }>("/payment/buy-credits", data).then((r) => r.data);

// PDF Export
export const exportMenuPdf = (venueId: string, menuId: string, options?: Record<string, unknown>) =>
  api.post(`/venues/${venueId}/menus/${menuId}/export-pdf`, options || {}, { responseType: "blob" }).then((r) => r.data);

// Categories
export const getCategories = (menuId: string) =>
  api.get<{ categories: Category[] }>(`/menus/${menuId}/categories`).then((r) => r.data.categories);

export const createCategory = (menuId: string, data: { name: string; description?: string }) =>
  api.post<{ category: Category }>(`/menus/${menuId}/categories`, data).then((r) => r.data.category);

export const updateCategory = (id: string, data: Partial<Category>) =>
  api.patch<{ category: Category }>(`/categories/${id}`, data).then((r) => r.data.category);

export const deleteCategory = (id: string) =>
  api.delete(`/categories/${id}`).then((r) => r.data);

export const reorderCategories = (menuId: string, ids: string[]) =>
  api.patch(`/menus/${menuId}/categories/reorder`, { ids }).then((r) => r.data);

// Items
export const getItems = (categoryId: string) =>
  api.get<{ items: Item[] }>(`/categories/${categoryId}/items`).then((r) => r.data.items);

export const createItem = (categoryId: string, data: Partial<Item>) =>
  api.post<{ item: Item }>(`/categories/${categoryId}/items`, data).then((r) => r.data.item);

export const updateItem = (id: string, data: Partial<Item>) =>
  api.patch<{ item: Item }>(`/items/${id}`, data).then((r) => r.data.item);

export const deleteItem = (id: string) =>
  api.delete(`/items/${id}`).then((r) => r.data);

export const toggleAvailability = (id: string) =>
  api.patch<{ item: Item }>(`/items/${id}/availability`).then((r) => r.data.item);

export const reorderItems = (categoryId: string, ids: string[]) =>
  api.patch(`/categories/${categoryId}/items/reorder`, { ids }).then((r) => r.data);

export const bulkUpdatePrices = (venueId: string, percentage: number, rounding: string = "none") =>
  api.patch<{ success: boolean; updated: number }>(`/venues/${venueId}/items/bulk-price`, { percentage, rounding }).then((r) => r.data);

export const getVenueLogs = (venueId: string, params?: { limit?: number; offset?: number; type?: string }) =>
  api.get<{ logs: VenueLog[]; total: number }>(`/venues/${venueId}/logs`, { params }).then((r) => r.data);

export interface VenueLog {
  _id: string;
  type: string;
  level: string;
  message: string;
  meta?: Record<string, unknown>;
  createdAt: string;
}

// Ingredients
export const searchIngredients = (search = "", lang = "tr") =>
  api.get<{ ingredients: Ingredient[] }>(`/ingredients`, { params: { search, lang } }).then((r) => r.data.ingredients);

// Upload
export const uploadImage = (file: File, type: "item" | "logo" | "cover" | "category" = "item", id?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams({ type });
  if (id) params.set("id", id);
  return api
    .post<{ url: string }>(`/upload/image?${params}`, formData)
    .then((r) => r.data.url);
};

export const uploadVideo = (file: File, id?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  if (id) params.set("id", id);
  return api
    .post<{ url: string }>(`/upload/video?${params}`, formData)
    .then((r) => r.data.url);
};

// Theme
export const getTheme = (venueId: string) =>
  api.get<ThemeResponse>(`/venues/${venueId}/theme`).then((r) => r.data);

export const updateTheme = (venueId: string, data: Partial<VenueTheme>) =>
  api.put<{ theme: VenueTheme }>(`/venues/${venueId}/theme`, data).then((r) => r.data.theme);

// QR Codes
export const getQrCodes = (venueId: string) =>
  api.get<{ qrCodes: QrCode[] }>(`/venues/${venueId}/qr-codes`).then((r) => r.data.qrCodes);

export const createQrCode = (venueId: string, data: Partial<QrCode>) =>
  api.post<{ qrCode: QrCode }>(`/venues/${venueId}/qr-codes`, data).then((r) => r.data.qrCode);

export const deleteQrCode = (id: string) =>
  api.delete(`/qr-codes/${id}`).then((r) => r.data);

export const downloadQrCode = (id: string, format: "png" | "svg" = "png") =>
  api.get(`/qr-codes/${id}/download?format=${format}`, { responseType: "blob" }).then((r) => r.data);

export const previewQrCode = (id: string) =>
  api.get(`/qr-codes/${id}/preview`, { responseType: "blob" }).then((r) => r.data);

// Orders
export const getOrders = (venueId: string, status?: string) =>
  api.get<{ orders: Order[] }>(`/venues/${venueId}/orders`, { params: { status } }).then((r) => r.data.orders);

export const updateOrderStatus = (orderId: string, status: OrderStatus) =>
  api.patch<{ order: Order }>(`/orders/${orderId}/status`, { status }).then((r) => r.data.order);

// Tables
export interface TableItem {
  id: string;
  _id?: string;
  venueId: string;
  name: string;
  status: "empty" | "occupied" | "waiting_bill";
  qrCodeId?: QrCode;
  sortOrder: number;
  activeOrders: number;
  totalOrders: number;
  totalAmount: number;
  pendingCount: number;
  preparingCount: number;
  deliveredCount: number;
}

export const getTables = (venueId: string) =>
  api.get<{ tables: TableItem[] }>(`/venues/${venueId}/tables`).then((r) => r.data.tables);

export const getTableDetail = (id: string) =>
  api.get<{ table: TableItem; orders: Order[] }>(`/tables/${id}`).then((r) => r.data);

export const createTable = (venueId: string, data: { name: string }) =>
  api.post<{ table: TableItem }>(`/venues/${venueId}/tables`, data).then((r) => r.data.table);

export const updateTable = (id: string, data: { name?: string; status?: string }) =>
  api.patch<{ table: TableItem }>(`/tables/${id}`, data).then((r) => r.data.table);

export const assignQrToTable = (id: string, qrCodeId: string | null) =>
  api.patch<{ table: TableItem }>(`/tables/${id}/assign-qr`, { qrCodeId }).then((r) => r.data.table);

export const deleteTable = (id: string) =>
  api.delete(`/tables/${id}`).then((r) => r.data);

export const getVenueAllItems = (venueId: string) =>
  api.get<{ categories: { _id: string; name: string; items: { _id: string; name: string; price: number; image?: string }[] }[] }>(`/venues/${venueId}/all-items`).then((r) => r.data.categories);

export const createTableOrder = (tableId: string, data: { items: { itemId: string; quantity: number; note?: string }[]; note?: string }) =>
  api.post<{ order: Order }>(`/tables/${tableId}/order`, data).then((r) => r.data.order);

export const closeBill = (tableId: string) =>
  api.post<{ bill: { _id: string; totalAmount: number; orderCount: number } }>(`/tables/${tableId}/close-bill`).then((r) => r.data.bill);

export interface SalesReport {
  period: string;
  totalRevenue: number;
  totalOrders: number;
  totalBills: number;
  avgBillAmount: number;
  dailyBreakdown: { date: string; revenue: number; orders: number; bills: number }[];
  topItems: { name: string; quantity: number; revenue: number }[];
  hourlyActivity: { hour: number; revenue: number; bills: number }[];
  tablePerformance: { tableName: string; revenue: number; bills: number }[];
  previousPeriod: { totalRevenue: number; totalOrders: number; totalBills: number };
}

export const getSalesReport = (venueId: string, period: string = "daily") =>
  api.get<SalesReport>(`/venues/${venueId}/sales`, { params: { period } }).then((r) => r.data);

// Staff
export interface StaffMember {
  _id: string;
  venueId: string;
  name: string;
  pin: string;
  role: "waiter" | "cashier";
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

export const getStaff = (venueId: string) =>
  api.get<{ staff: StaffMember[] }>(`/venues/${venueId}/staff`).then((r) => r.data.staff);

export const createStaff = (venueId: string, data: { name: string; role: string }) =>
  api.post<{ staff: StaffMember }>(`/venues/${venueId}/staff`, data).then((r) => r.data.staff);

export const deleteStaff = (id: string) =>
  api.delete(`/staff/${id}`).then((r) => r.data);

export const resetStaffPin = (id: string) =>
  api.patch<{ staff: StaffMember }>(`/staff/${id}/reset-pin`).then((r) => r.data.staff);

export const toggleStaffActive = (id: string) =>
  api.patch<{ staff: StaffMember }>(`/staff/${id}/toggle`).then((r) => r.data.staff);

export const staffLogin = (venueNumber: number, pin: string) =>
  api.post<{ token: string; staff: { id: string; name: string; role: string }; venue: { id: string; name: string; slug: string } }>("/auth/staff-login", { venueNumber, pin }).then((r) => r.data);

// Analytics
export const getAnalytics = (venueId: string) =>
  api.get<AnalyticsSummary>(`/venues/${venueId}/analytics`).then((r) => r.data);

export const getPopularItems = (venueId: string) =>
  api.get<{ popular: PopularItem[] }>(`/venues/${venueId}/analytics/popular`).then((r) => r.data.popular);

// Subscription
export const getPlans = () =>
  api.get<{ plans: PlanInfo[] }>("/subscription/plans").then((r) => r.data.plans);

export const getCurrentSubscription = () =>
  api.get<{ venues: Array<{ id: string; name: string; planType: string; planExpiresAt: string }> }>(
    "/subscription/current"
  ).then((r) => r.data.venues);

// Loyalty
export const getLoyaltyProgram = () =>
  api.get<{ program: LoyaltyProgram | null }>("/loyalty/program").then((r) => r.data.program);

export const saveLoyaltyProgram = (data: {
  name: string;
  scope?: "all_venues" | "per_venue";
  pointsPerCurrency: number;
  isActive?: boolean;
}) => api.post<{ program: LoyaltyProgram }>("/loyalty/program", data).then((r) => r.data.program);

export const getLoyaltyRules = () =>
  api.get<{ rules: LoyaltyRule[] }>("/loyalty/rules").then((r) => r.data.rules);

export const createLoyaltyRule = (data: Partial<LoyaltyRule>) =>
  api.post<{ rule: LoyaltyRule }>("/loyalty/rules", data).then((r) => r.data.rule);

export const updateLoyaltyRule = (id: string, data: Partial<LoyaltyRule>) =>
  api.patch<{ rule: LoyaltyRule }>(`/loyalty/rules/${id}`, data).then((r) => r.data.rule);

export const deleteLoyaltyRule = (id: string) =>
  api.delete(`/loyalty/rules/${id}`).then((r) => r.data);

export const getLoyaltyCustomers = (params?: { limit?: number; offset?: number }) =>
  api
    .get<{ customers: LoyaltyCustomer[]; total: number }>("/loyalty/customers", { params })
    .then((r) => r.data);

// Reviews
export const getVenueReviews = (
  venueId: string,
  params?: { itemId?: string; rating?: number; hidden?: "true" | "false"; limit?: number; offset?: number }
) =>
  api
    .get<{ reviews: Review[]; total: number; limit: number; offset: number }>(
      `/venues/${venueId}/reviews`,
      { params }
    )
    .then((r) => r.data);

export const hideReview = (id: string) =>
  api.patch<{ review: Review }>(`/reviews/${id}/hide`).then((r) => r.data.review);

export const unhideReview = (id: string) =>
  api.patch<{ review: Review }>(`/reviews/${id}/unhide`).then((r) => r.data.review);

export const deleteReview = (id: string) =>
  api.delete(`/reviews/${id}`).then((r) => r.data);
